import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { fetchCN } from "../../app/services/OrdersService";
import PageWrapper from "../../app/shared/common/PageWrapper";
import CreateTable from "../../app/shared/common/table/CreateTable";

const tableColumns = [
  { key: "credit_note_number", label: "CN Number" },
  { key: "amount", label: "Amount" },
  { key: "createdAt", label: "Created At" },
];

// Type for credit note
type CreditNote = {
  id: any;
  credit_note_number: string;
  amount: number;
  createdAt: string;
  created_at: string; // raw date string from API
  salesReturn: any;
};

const CNList = () => {
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Popup
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [selectedCN, setSelectedCN] = useState<CreditNote | null>(null);

  const loadCreditNotes = async () => {
    try {
      setLoading(true);
      const res = await fetchCN({});

      if (res.status === "success") {
        const mappedData: CreditNote[] = res.data.map((item: any) => ({
          id: item.id,
          credit_note_number: item.credit_note_number,
          amount: item.amount,
          createdAt: new Date(item.created_at).toLocaleString(),
          created_at: item.created_at, // keep original for sorting
          salesReturn: item.salesReturn,
        }));

        // Sort descending by created_at
        mappedData.sort((a: CreditNote, b: CreditNote) => {
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });

        setCreditNotes(mappedData);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error("Failed to load Credit Notes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCreditNotes();
  }, []);

  // Open Popup
  const handleOpenPopup = (row: CreditNote) => {
    const items = row.salesReturn?.history?.items || [];
    setSelectedItems(items);
    setSelectedCN(row);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItems([]);
    setSelectedCN(null);
  };

  return (
    <PageWrapper title="Credit Note List">
      {loading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <CircularProgress />
        </div>
      ) : (
        <CreateTable
          data={creditNotes}
          columns={tableColumns}
          loading={loading}
          customRender={{
            amount: (value: any, row: CreditNote) => (
              <Button variant="text" onClick={() => handleOpenPopup(row)}>
                ₹{value}
              </Button>
            ),
          }}
        />
      )}

      {/* Popup Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          CN Details — {selectedCN?.credit_note_number} (₹{selectedCN?.amount})
        </DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product Name</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Amount (₹)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedItems.length > 0 ? (
                selectedItems.map((it: any) => (
                  <TableRow key={it.id}>
                    <TableCell>{it.product?.name || "—"}</TableCell>
                    <TableCell>{it.quantity}</TableCell>
                    <TableCell>
                      {Number(it.product?.base_price || 0) * Number(it.quantity || 0)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No items found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
};

export default CNList;
